/**
 * Division Service
 * API calls for division management
 */
import BaseApiService from '../base/BaseApiService';
import {
  Division,
  DivisionCreateData,
  DivisionUpdateData,
  DivisionVersionHistoryEntry,
  DivisionFilters,
  DivisionsResponse,
  UserDivisionChangeImpact
} from '@app-types/division';

class DivisionService extends BaseApiService {
  /**
   * Get all divisions with filters, pagination, sorting
   */
  async getAll(filters: DivisionFilters = {}, page = 1, limit = 50): Promise<DivisionsResponse> {
    const params = this.buildQueryParams({
      ...filters,
      limit,
      offset: (page - 1) * limit
    });

    return this.get<DivisionsResponse>(`/admin/divisions?${params}`);
  }

  /**
   * Get active divisions for dropdown
   */
  async getActive(): Promise<{ id: number; name: string }[]> {
    const response = await this.get<{ data: { id: number; name: string }[] }>('/divisions/active');
    return response.data;
  }

  /**
   * Get division by ID
   */
  async getById(id: number): Promise<Division> {
    const response = await this.get<{ data: Division }>(`/admin/divisions/${id}`);
    return response.data;
  }

  /**
   * Every version of a division (#1163), newest first, each with its status and who last changed it
   */
  async getVersions(id: number): Promise<DivisionVersionHistoryEntry[]> {
    const response = await this.get<{ data: DivisionVersionHistoryEntry[] }>(`/admin/divisions/${id}/versions`);
    return response.data;
  }

  /**
   * Create new division
   */
  async create(data: DivisionCreateData): Promise<Division> {
    const response = await this.post<{ data: Division }>('/admin/divisions', data);
    return response.data;
  }

  /**
   * Save changes as a Division Version (#937): name, description, L1/L2/L3 leads,
   * Effective Date and note. Leads change only here.
   */
  async update(id: number, data: DivisionUpdateData): Promise<Division> {
    const response = await this.put<{ data: Division }>(`/admin/divisions/${id}`, data);
    return response.data;
  }

  /**
   * Deactivate division
   */
  async deactivate(id: number): Promise<{ data: Division; message: string }> {
    return this.post<{ data: Division; message: string }>(`/admin/divisions/${id}/deactivate`);
  }

  /**
   * Reactivate division
   */
  async reactivate(id: number): Promise<{ data: Division; message: string }> {
    return this.post<{ data: Division; message: string }>(`/admin/divisions/${id}/reactivate`);
  }

  /**
   * Delete division
   */
  async deleteDivision(id: number): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/admin/divisions/${id}`);
  }

  /**
   * Get user division change impact
   */
  async getUserDivisionChangeImpact(userId: number): Promise<UserDivisionChangeImpact> {
    const response = await this.get<{ data: UserDivisionChangeImpact }>(`/admin/divisions/users/${userId}/impact`);
    return response.data;
  }
}

const divisionService = new DivisionService();
export default divisionService;
